import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import {
  ApiError,
  api,
  type BrokerSymbol,
  type ManualTradeInput,
  type MarketConversion,
  type TradePreviewResponse,
} from "@/lib/api";
import { SymbolCombobox } from "@/components/SymbolCombobox";
import { queryKeys } from "@/lib/queryClient";
import { useCurrency } from "@/hooks/useCurrency";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatMoney, formatPips } from "@/lib/format";
import { pushToast } from "@/lib/toast";
import { useQuote } from "@/hooks/useQuotes";
import { pipsFromPrice, priceFromPips, type PipsContext } from "@/lib/pips";

type EntryMode = "market" | "pending";
type LevelMode = "price" | "pips";

/**
 * Andy types in a trade; nothing here decides limit-vs-stop — that is derived server-side
 * from which side of the quote the price sits on (§21 review), so this screen only ever asks
 * "market, or pending at a price?".
 */
export function NewTrade() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // M26a — money-at-risk in the account's own currency, not a hardcoded "$".
  const accountCurrency = useCurrency();

  const [symbol, setSymbol] = useState("");
  // The picked broker spec — pip size, digits, stops level. Cleared the moment the text
  // drifts from the pick, so nothing downstream trusts a spec for a symbol no longer shown.
  const [selectedSpec, setSelectedSpec] = useState<BrokerSymbol | null>(null);
  const [direction, setDirection] = useState<"buy" | "sell">("buy");
  const [entryMode, setEntryMode] = useState<EntryMode>("market");
  const [entryPriceText, setEntryPriceText] = useState("");
  // slText/tpText stay the canonical ABSOLUTE PRICES the preview and submit read — pips are
  // an input aid converted at typing time, never a unit that crosses the wire (lib/pips.ts).
  const [slText, setSlText] = useState("");
  const [tpText, setTpText] = useState("");
  const [slMode, setSlMode] = useState<LevelMode>("price");
  const [tpMode, setTpMode] = useState<LevelMode>("price");
  const [slPipsText, setSlPipsText] = useState("");
  const [tpPipsText, setTpPipsText] = useState("");
  const [trailingText, setTrailingText] = useState("");
  // M24b — empty means "use the global lot policy", which is what every manual trade did
  // before this field existed. Kept as text so a half-typed "0." is not read as a size.
  const [volumeText, setVolumeText] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);

  const entryPrice = entryMode === "market" ? null : parseNum(entryPriceText);
  const sl = parseNum(slText);
  const tp = parseNum(tpText);
  const trailingPips = parseNum(trailingText);
  const volume = parseNum(volumeText);

  const ready =
    symbol.trim().length > 0 && sl !== null && tp !== null && (entryMode === "market" || entryPrice !== null);

  // Debounced so a live quote/lot lookup does not fire on every keystroke.
  const [debounced, setDebounced] = useState<ManualTradeInput | null>(null);
  useEffect(() => {
    if (!ready) {
      setDebounced(null);
      return;
    }
    const handle = setTimeout(() => {
      setDebounced({
        symbol: symbol.trim(),
        direction,
        entryPrice,
        sl: sl!,
        tp: tp!,
        ...(trailingPips !== null && trailingPips > 0 ? { trailingPips } : {}),
        // Omitted entirely rather than sent as the policy's number: the server owns the
        // default, so an empty field cannot pin a stale policy value into the trade.
        ...(volume !== null && volume > 0 ? { volume } : {}),
      });
    }, 400);
    return () => clearTimeout(handle);
  }, [ready, symbol, direction, entryMode, entryPrice, sl, tp, trailingPips, volume]);

  const preview = useQuery({
    queryKey: ["trade-preview", debounced],
    queryFn: () => api.tradePreview(debounced!),
    enabled: debounced !== null,
    retry: false,
  });

  // The broker's own instrument list; only changes on a spec resync. Empty when the
  // metaapi module is off, which degrades the combobox to a plain input.
  const symbolsQuery = useQuery({ queryKey: queryKeys.symbols, queryFn: api.symbols, staleTime: 5 * 60_000 });
  const brokerSymbols = symbolsQuery.data?.symbols ?? [];

  // M17 — subscribe-on-select. One deliberate pick = one warm; the server side is
  // idempotent, so re-picking a symbol costs nothing. Fire-and-forget: the ws stream is
  // what actually delivers prices, this just asks the broker to start it.
  const warm = useMutation({ mutationFn: api.quotesWarm });
  const tick = useQuote(selectedSpec?.symbol);

  // The reference pips are measured from: MT4's convention — current price for a market
  // order (buy executes at ask, sell at bid), the typed entry for a pending one.
  const refPrice =
    entryMode === "pending" ? entryPrice : tick && !tick.stale ? (direction === "buy" ? tick.ask : tick.bid) : null;
  const pipsCtx: PipsContext | null =
    selectedSpec?.pipSize && refPrice !== null
      ? { direction, referencePrice: refPrice, pipSize: selectedSpec.pipSize, digits: selectedSpec.digits }
      : null;

  // Pips mode needs a pip size and a reference; when either goes away (spec cleared,
  // quote gone stale in market mode), fall back to price entry rather than converting
  // against a reference that no longer exists.
  const pipsReady = pipsCtx !== null;
  useEffect(() => {
    if (!pipsReady) {
      setSlMode("price");
      setTpMode("price");
    }
  }, [pipsReady]);

  const previewError = preview.error instanceof ApiError ? preview.error : null;
  const candidates = (previewError?.details.candidates as string[] | undefined) ?? [];

  const submit = useMutation({
    mutationFn: (body: ManualTradeInput & { requestId: string }) => api.trade(body),
    onSuccess: (res) => {
      setConfirmOpen(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.positions });
      pushToast(res.replay ? `Trade already placed (order #${res.orderId})` : `Trade queued (order #${res.orderId})`);
      navigate("/trades");
    },
  });

  const submitError = submit.error instanceof ApiError ? submit.error : null;

  const openConfirm = () => {
    setRequestId(crypto.randomUUID());
    setConfirmOpen(true);
  };

  const plan = preview.data;

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">New trade</h1>

      <Card>
        <CardContent className="space-y-3 p-4">
          <Field label="Symbol">
            <SymbolCombobox
              value={symbol}
              symbols={brokerSymbols}
              onChange={(text) => {
                setSymbol(text);
                if (selectedSpec && text.trim().toUpperCase() !== selectedSpec.symbol.toUpperCase()) {
                  setSelectedSpec(null);
                }
              }}
              onSelect={(spec) => {
                setSymbol(spec.symbol);
                setSelectedSpec(spec);
                warm.mutate(spec.symbol);
              }}
              placeholder="e.g. XAUUSD, EURUSD"
            />
          </Field>

          {selectedSpec && (
            <div className="flex items-center gap-4 text-xs">
              {tick && !tick.stale ? (
                <>
                  <span className="text-muted-foreground">
                    Bid <span className="font-mono text-foreground">{priceText(tick.bid, selectedSpec.digits)}</span>
                  </span>
                  <span className="text-muted-foreground">
                    Ask <span className="font-mono text-foreground">{priceText(tick.ask, selectedSpec.digits)}</span>
                  </span>
                  <span className="ml-auto flex items-center gap-1 text-success">
                    <span className="h-1.5 w-1.5 rounded-full bg-success" /> live
                  </span>
                </>
              ) : (
                // Honest, not decorative: no tick or a stale one is "no live price", never
                // a frozen number pretending to move.
                <span className="text-muted-foreground">
                  {warm.isPending ? "Waking the price stream…" : "No live price — market may be closed."}
                </span>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Field label="Direction">
              <Select value={direction} onChange={(e) => setDirection(e.target.value as "buy" | "sell")}>
                <option value="buy">Buy</option>
                <option value="sell">Sell</option>
              </Select>
            </Field>
            <Field label="Entry">
              <Select value={entryMode} onChange={(e) => setEntryMode(e.target.value as EntryMode)}>
                <option value="market">Market</option>
                <option value="pending">Pending @ price</option>
              </Select>
            </Field>
          </div>

          {entryMode === "pending" && (
            <Field label="Entry price">
              <Input
                type="number"
                step="any"
                value={entryPriceText}
                onChange={(e) => setEntryPriceText(e.target.value)}
                placeholder="Price to rest the order at"
              />
            </Field>
          )}

          <div className="grid grid-cols-2 gap-2">
            <LevelField
              label="Stop loss"
              kind="sl"
              mode={slMode}
              priceText={slText}
              pipsText={slPipsText}
              ctx={pipsCtx}
              onModeChange={(mode) => {
                setSlMode(mode);
                if (mode === "pips") setSlPipsText(pipsCtx && sl !== null ? String(pipsFromPrice("sl", sl, pipsCtx).pips) : "");
              }}
              onPriceText={setSlText}
              onPipsText={(text) => {
                setSlPipsText(text);
                const pips = parseNum(text);
                setSlText(pipsCtx && pips !== null ? String(priceFromPips("sl", pips, pipsCtx)) : "");
              }}
            />
            <LevelField
              label="Take profit"
              kind="tp"
              mode={tpMode}
              priceText={tpText}
              pipsText={tpPipsText}
              ctx={pipsCtx}
              onModeChange={(mode) => {
                setTpMode(mode);
                if (mode === "pips") setTpPipsText(pipsCtx && tp !== null ? String(pipsFromPrice("tp", tp, pipsCtx).pips) : "");
              }}
              onPriceText={setTpText}
              onPipsText={(text) => {
                setTpPipsText(text);
                const pips = parseNum(text);
                setTpText(pipsCtx && pips !== null ? String(priceFromPips("tp", pips, pipsCtx)) : "");
              }}
            />
          </div>

          <Field label="Lot size — optional">
            <Input
              type="number"
              step="any"
              min="0"
              aria-label="Lot size"
              value={volumeText}
              onChange={(e) => setVolumeText(e.target.value)}
              placeholder={
                plan?.policyVolume !== null && plan?.policyVolume !== undefined
                  ? `empty = policy (${plan.policyVolume})`
                  : "empty = the global lot policy"
              }
            />
            {plan?.volumeSource === "override" && (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <p className="text-xs text-yellow-500">
                  ⚠️ Overriding the policy{plan.policyVolume !== null ? ` of ${plan.policyVolume}` : ""} for this trade
                  only.
                </p>
                <button
                  type="button"
                  onClick={() => setVolumeText("")}
                  className="text-xs text-muted-foreground underline underline-offset-2"
                >
                  Use the policy size
                </button>
              </div>
            )}
          </Field>

          <Field label="Trailing stop (pips) — optional">
            <Input
              type="number"
              step="any"
              value={trailingText}
              onChange={(e) => setTrailingText(e.target.value)}
              placeholder="empty = none"
            />
            {trailingPips !== null && trailingPips > 0 && (
              <p className="mt-1 text-xs text-yellow-500">
                Once set, MetaApi's servers own this stop — the app cannot move it afterwards.
              </p>
            )}
          </Field>
        </CardContent>
      </Card>

      {debounced !== null && (
        <Card>
          <CardContent className="space-y-2 p-4 text-sm">
            {preview.isLoading ? (
              <p className="text-muted-foreground">Checking…</p>
            ) : previewError ? (
              <div className="space-y-1">
                <p className="text-destructive">{previewError.message}</p>
                {candidates.length > 0 && (
                  <p className="text-xs text-muted-foreground">Did you mean: {candidates.join(", ")}?</p>
                )}
              </div>
            ) : plan ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="font-medium">
                    {plan.brokerSymbol} · {plan.entryType}
                    {plan.requestedPrice !== null ? ` @ ${plan.requestedPrice}` : ""}
                  </span>
                  {plan.mode === "dry_run" && <Badge variant="outline">dry run</Badge>}
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <Field label="Lot size" value={plan.volume === null ? "—" : String(plan.volume)} />
                  <Field label="Stop distance" value={formatPips(plan.stopPips)} />
                  <Field label="Money at risk" value={formatMoney(plan.moneyAtRisk, accountCurrency)} />
                  <Field label="Profit at TP" value={formatMoney(plan.moneyAtTp, accountCurrency)} />
                </div>
                {/* M24c — louder than the generic warning list below, and deliberately so.
                    Every other warning is about a trade that will do what was asked; this one
                    says the trade will do something ELSE. It is the only line here that
                    changes what Andy is agreeing to. */}
                {plan.marketConversion && <MarketConversionNotice conversion={plan.marketConversion} />}
                {/* M24b — every adjustment the broker's grid forced on a typed size. A
                    silently-resized trade is precisely the surprise a per-trade volume field
                    exists to remove. */}
                {plan.lotNotes.map((note) => (
                  <p key={note} className="text-xs text-yellow-500">
                    {note}
                  </p>
                ))}
                {plan.warnings.map((w) => (
                  <p key={w} className="text-xs text-muted-foreground">
                    ⚠️ {w}
                  </p>
                ))}
                {!plan.allowed && (
                  <div className="space-y-1 pt-1">
                    {plan.failures.map((f) => (
                      <p key={f} className="text-xs text-destructive">
                        {f}
                      </p>
                    ))}
                  </div>
                )}
              </>
            ) : null}
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        No rules run on manual trades — the stop loss and take profit set here are the only automation. There is no
        signal behind this trade for TP1→breakeven or any other management step to key off.
      </p>

      <Button className="w-full" disabled={!plan || !plan.allowed} onClick={openConfirm}>
        Review trade
      </Button>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm trade</DialogTitle>
            <DialogDescription>
              {plan && (
                <span className="block space-y-1 text-left">
                  <span className="block">
                    <b>{direction.toUpperCase()}</b> {plan.brokerSymbol} · {plan.entryType}
                    {plan.requestedPrice !== null ? ` @ ${plan.requestedPrice}` : ""} · {plan.volume ?? "—"} lots
                  </span>
                  <span className="block">
                    SL {sl} · TP {tp} · risk {formatMoney(plan.moneyAtRisk, accountCurrency)} · profit at TP {formatMoney(plan.moneyAtTp, accountCurrency)}
                  </span>
                  {plan.trailing && (
                    <span className="block font-medium">
                      Trailing stop {plan.trailing.distancePips} pips — once set, MetaApi's servers own this stop;
                      the app cannot move it afterwards.
                    </span>
                  )}
                  {plan.mode === "dry_run" && <span className="block font-medium">Dry run — no real order will be sent.</span>}
                  {plan.entryType !== "market" && (
                    <span className="block">
                      This broker has no GTC — the pending order expires automatically if unfilled (default ~48h).
                    </span>
                  )}
                  <span className="block">No rules run on this trade — the SL/TP above are the only automation.</span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {submitError && <p className="text-sm text-destructive">{submitError.message}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={submit.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!debounced || !requestId) return;
                submit.mutate({ ...debounced, requestId });
              }}
              disabled={submit.isPending || !debounced || !requestId}
            >
              {submit.isPending ? "Placing…" : placeLabel(plan)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * One SL/TP entry: a price input, or a pips input converted to the canonical price as you
 * type. A price on the wrong side of the market is warned about, never silently flipped.
 */
function LevelField({
  label,
  kind,
  mode,
  priceText: priceValue,
  pipsText,
  ctx,
  onModeChange,
  onPriceText,
  onPipsText,
}: {
  label: string;
  kind: "sl" | "tp";
  mode: LevelMode;
  priceText: string;
  pipsText: string;
  ctx: PipsContext | null;
  onModeChange: (mode: LevelMode) => void;
  onPriceText: (text: string) => void;
  onPipsText: (text: string) => void;
}) {
  const price = parseNum(priceValue);
  const wrongSide = ctx !== null && price !== null && pipsFromPrice(kind, price, ctx).wrongSide;

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <label className="block text-xs text-muted-foreground">{label}</label>
        <button
          type="button"
          className="text-[11px] text-muted-foreground underline decoration-dotted disabled:opacity-40"
          // Pips need a pip size (a picked spec) and a reference price (live quote or a
          // typed pending entry) — until then the toggle explains itself by being disabled.
          disabled={ctx === null}
          onClick={() => onModeChange(mode === "price" ? "pips" : "price")}
        >
          {mode === "price" ? "enter in pips" : "enter a price"}
        </button>
      </div>
      {mode === "price" ? (
        <Input type="number" step="any" value={priceValue} onChange={(e) => onPriceText(e.target.value)} />
      ) : (
        <>
          <Input type="number" step="any" value={pipsText} onChange={(e) => onPipsText(e.target.value)} placeholder="pips" />
          <p className="mt-1 text-xs text-muted-foreground">= {priceValue === "" ? "—" : priceValue}</p>
        </>
      )}
      {wrongSide && (
        <p className="mt-1 text-xs text-yellow-500">
          {kind === "sl" ? "This stop is on the profit side of the market." : "This target is on the losing side of the market."}
        </p>
      )}
    </div>
  );
}

function priceText(value: number, digits: number | null): string {
  return digits === null ? String(value) : value.toFixed(digits);
}

function parseNum(text: string): number | null {
  if (text.trim() === "") return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

function Field({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
  if (children) {
    return (
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">{label}</label>
        {children}
      </div>
    );
  }
  return (
    <div>
      <span className="text-muted-foreground">{label}: </span>
      <span className="font-mono text-foreground">{value}</span>
    </div>
  );
}

/**
 * Names the button after what will actually happen at the broker.
 *
 * "Place trade" is true of everything and therefore says nothing at the one moment it
 * matters: a pending order that has been converted to market. The label is derived from
 * `entryType`, which is the server's post-conversion verdict, so it cannot disagree with the
 * order that gets placed.
 */
function placeLabel(plan: TradePreviewResponse | null | undefined): string {
  if (!plan) return "Place trade";
  return plan.entryType === "market" ? "Place market order" : "Place pending order";
}

/**
 * The one warning on this screen that changes what is being agreed to.
 *
 * `near_market` quotes the measured distance — "close to the market" with no number is not
 * something Andy can check against the price he typed. `no_quote` must NOT quote a distance,
 * because there was nothing to measure: reporting an unmeasured conversion as a measured one
 * would be the screen inventing the fact the warning exists to convey.
 */
function MarketConversionNotice({ conversion }: { conversion: MarketConversion }) {
  return (
    <p className="rounded-md border border-primary/50 bg-primary/10 p-2 text-xs font-medium text-foreground">
      {conversion.reason === "near_market" ? (
        <>
          ⚠️ This will fill immediately as a market order, not rest. Your price is{" "}
          {conversion.distancePips} pips from the market, inside this symbol&apos;s{" "}
          {conversion.thresholdPips}-pip threshold.
        </>
      ) : (
        <>
          ⚠️ This will fill immediately as a market order, not rest — there is no live quote to
          measure your price against, so the entry cannot be rested safely.
        </>
      )}
    </p>
  );
}
